import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CHAT_MESSAGES, CHAT_MILESTONES } from '../data/chatData';
import { ChatViewer } from '../components/chat/ChatViewer';
import { AnalysisView } from '../components/chat/AnalysisView';

export const ChatPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'analysis' ? 'analysis' : 'chat';
  const [activeTab, setActiveTab] = useState<'chat' | 'analysis'>(initialTab);
  const [activeMilestone, setActiveMilestone] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'analysis' && activeTab !== 'analysis') {
      setActiveTab('analysis');
    } else if (tabParam !== 'analysis' && activeTab === 'analysis' && !tabParam) {
      setActiveTab('chat');
    }
  }, [searchParams]);

  const handleTabSwitch = (tab: 'chat' | 'analysis') => {
    setActiveTab(tab);
    if (tab === 'analysis') {
      setSearchParams({ tab: 'analysis' });
    } else {
      setSearchParams({});
    }
  };

  return (
    <main className="chat-page" role="main">
      {/* Top Header */}
      <header className="chat-header">
        <div className="chat-header-main">
          {/* Header Left: Option 1 (Go back to home/keepsake) + Partner Profile */}
          <div className="chat-header-left">
            <Link
              to="/"
              className="chat-nav-btn"
              title="Return to the main keepsake page"
              aria-label="Go back to home the main page"
            >
              <span>←</span>
              <span>Keepsake</span>
            </Link>

            <div className="chat-partner-profile">
              <div className="chat-avatar" aria-hidden="true">
                A
                <span className="chat-avatar-status" title="Archived history" />
              </div>

              <div className="chat-partner-info">
                <span className="chat-partner-name">Ankita Aich</span>
                <span className="chat-partner-status">
                  <span className="badge-archive">Chronological Stream</span>
                  <span>• 33,186 Records</span>
                </span>
              </div>
            </div>
          </div>

          {/* Header Right: Option 2 (Toggle between Chat & Critical Analysis) + Search */}
          <div className="chat-header-actions">
            <div className="chat-toggle-pill" role="tablist" aria-label="View switcher">
              <button
                type="button"
                className={`chat-toggle-option ${activeTab === 'chat' ? 'active' : ''}`}
                onClick={() => handleTabSwitch('chat')}
                role="tab"
                aria-selected={activeTab === 'chat'}
                title="View chronological chat messages"
              >
                💬 Chat
              </button>
              <button
                type="button"
                className={`chat-toggle-option ${activeTab === 'analysis' ? 'active' : ''}`}
                onClick={() => handleTabSwitch('analysis')}
                role="tab"
                aria-selected={activeTab === 'analysis'}
                title="View dynamic relationship analysis & Ask AI"
              >
                ✦ AI &amp; Analysis
              </button>
            </div>

            {activeTab === 'chat' && (
              <button
                type="button"
                className="chat-search-toggle"
                onClick={() => setIsSearchOpen(prev => !prev)}
                title="Search conversation"
                aria-label="Toggle message search"
              >
                🔍
              </button>
            )}
          </div>
        </div>

        {/* Search input field if open */}
        {activeTab === 'chat' && isSearchOpen && (
          <div className="chat-search-container">
            <span>🔍</span>
            <input
              type="text"
              className="chat-search-input"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search chat history (text, date, media)..."
              autoFocus
            />
            {searchQuery && (
              <button
                type="button"
                className="chat-search-close"
                onClick={() => setSearchQuery('')}
              >
                ✕
              </button>
            )}
          </div>
        )}

        {/* Milestone Quick Jump Bar (Visible during Chat view) */}
        {activeTab === 'chat' && (
          <nav className="chat-milestone-bar" aria-label="Milestone navigation">
            {CHAT_MILESTONES.map(ms => (
              <button
                key={ms.id}
                type="button"
                className={`milestone-pill ${activeMilestone === ms.id ? 'active' : ''}`}
                onClick={() => setActiveMilestone(ms.id)}
                title={ms.description}
              >
                {ms.title}
              </button>
            ))}
          </nav>
        )}
      </header>

      {/* Main Content Area: Chat Stream or Critical Analysis */}
      {activeTab === 'chat' ? (
        <ChatViewer
          messages={CHAT_MESSAGES}
          milestones={CHAT_MILESTONES}
          activeMilestone={activeMilestone}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onGoToAnalysis={() => handleTabSwitch('analysis')}
        />
      ) : (
        <AnalysisView onBackToChat={() => handleTabSwitch('chat')} />
      )}
    </main>
  );
};
